import React, { createContext, useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAppSelector } from 'reduxStore/store'
import { IClinicalTrialResponse } from 'types'
import {
  deleteClinicalTrialInfo,
  getClinicalTrialInfo,
  postClinicalTrialInfo,
  updateClinicalTrialInfo
} from 'services/clinical_trial'
import { useToast } from './toast'

interface IClinicalTrialContext {
  clinicalTrial?: IClinicalTrialResponse
  state?: IStateStdTypes
  updateStatus: (successMessage?: string) => Promise<void>
  updateStatusTo: (
    newStatus: ClinicalTrialStatus,
    successMessage?: string
  ) => Promise<void>
  deleteClinicalTrial: (
    status: 'success' | 'discarded',
    successMessage?: string
  ) => Promise<void>
  addNewClinicalTrial: () => Promise<void>
}

type ClinicalTrialStatus = IClinicalTrialResponse['status']

export const statusIndex: Record<ClinicalTrialStatus, number> = {
  active: 0,
  pending_watch_return: 1,
  success: 2,
  discarded: 2
}

const statusFlow: Record<ClinicalTrialStatus, ClinicalTrialStatus> = {
  active: 'pending_watch_return',
  pending_watch_return: 'success',
  success: 'active',
  discarded: 'active'
}

export const isClinicalTrialFinished = (
  status?: ClinicalTrialStatus
): status is Extract<ClinicalTrialStatus, 'success' | 'discarded'> =>
  status === 'success' || status === 'discarded'

const ClinicalTrialContext = createContext<IClinicalTrialContext>(
  {} as IClinicalTrialContext
)

const ClinicalTrialProvider: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  const { patientId } = useParams<{ patientId: string }>()
  const [state, setState] = useState<IStateStdTypes>('idle')
  const [clinicalTrial, setClinicalTrial] = useState<IClinicalTrialResponse>()
  const toast = useToast()
  const establishmentId = useAppSelector(
    state => state.establishment.data?.establishment_id
  )

  const fetchClinicalTrialStatus = async () => {
    if (state !== 'loading') {
      setState('loading')
      const response = await getClinicalTrialInfo({
        establishment_id: establishmentId ?? '',
        patient_id: patientId ?? ''
      })

      if (response.success) {
        setClinicalTrial(response.data)
        setState('success')
      } else {
        setState('error')
      }
    }
  }

  useEffect(() => {
    if (establishmentId) {
      fetchClinicalTrialStatus()
    }
  }, [establishmentId, patientId])

  const updateStatus = async (successMessage?: string) => {
    if (state !== 'loading' && clinicalTrial) {
      setState('loading')
      const currentStatus = clinicalTrial.status
      const nextStatus = statusFlow[currentStatus]

      const response = await updateClinicalTrialInfo({
        ...clinicalTrial,
        status: nextStatus
      })

      if (response.success) {
        setClinicalTrial({
          ...clinicalTrial,
          status: nextStatus
        })
        if (successMessage) {
          toast.handler({
            content: successMessage,
            severity: 'success'
          })
        } else if (isClinicalTrialFinished(nextStatus)) {
          toast.handler({
            content: 'O paciente foi removido do estudo',
            severity: 'success'
          })
        }
        setState('success')
      } else {
        toast.handler({
          content:
            'Houve um erro ao tentar atualizar o paciente, tente novamente',
          severity: 'error'
        })
        setState('error')
      }
    }
  }

  const updateStatusTo = async (
    newStatus: ClinicalTrialStatus,
    successMessage?: string
  ) => {
    if (state !== 'loading' && clinicalTrial) {
      setState('loading')

      const response = await updateClinicalTrialInfo({
        ...clinicalTrial,
        status: newStatus
      })

      if (response.success) {
        setClinicalTrial({
          ...clinicalTrial,
          status: newStatus
        })
        if (successMessage) {
          toast.handler({
            content: successMessage,
            severity: 'success'
          })
        } else if (isClinicalTrialFinished(newStatus)) {
          toast.handler({
            content: 'O paciente foi removido do estudo',
            severity: 'success'
          })
        }
        setState('success')
      } else {
        toast.handler({
          content:
            'Houve um erro ao tentar atualizar o paciente, tente novamente',
          severity: 'error'
        })
        setState('error')
      }
    }
  }

  const deleteClinicalTrial = async (
    status: 'success' | 'discarded',
    successMessage?: string
  ) => {
    if (state !== 'loading' && clinicalTrial) {
      setState('loading')

      const response = await deleteClinicalTrialInfo({
        establishment_id: clinicalTrial.establishment_id,
        patient_id: clinicalTrial.patient_id,
        clinical_status: status
      })

      if (response.success) {
        setClinicalTrial({
          ...clinicalTrial,
          status
        })

        toast.handler({
          content: successMessage ?? 'O paciente foi removido do estudo',
          severity: 'success'
        })
        setState('success')
      } else {
        toast.handler({
          content:
            'Houve um erro ao tentar atualizar o paciente, tente novamente',
          severity: 'error'
        })
        setState('error')
      }
    }
  }

  const addNewClinicalTrial = async () => {
    if (state !== 'loading') {
      setState('loading')

      const response = await postClinicalTrialInfo({
        establishment_id: establishmentId ?? '',
        patient_id: patientId ?? '',
        status: 'active'
      })

      if (response.success) {
        toast.handler({
          content: 'O paciente foi adicionado no estudo',
          severity: 'success'
        })
        setState('success')
        fetchClinicalTrialStatus()
      } else {
        toast.handler({
          content:
            'Houve um erro ao tentar adicionar o paciente, tente novamente',
          severity: 'error'
        })
        setState('error')
      }
    }
  }

  return (
    <ClinicalTrialContext.Provider
      value={{
        clinicalTrial,
        state,
        updateStatus,
        updateStatusTo,
        deleteClinicalTrial,
        addNewClinicalTrial
      }}
    >
      {children}
    </ClinicalTrialContext.Provider>
  )
}

export function useClinicalTrial() {
  return useContext(ClinicalTrialContext)
}

export default ClinicalTrialProvider
